import { Link, useHistory, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import type { SensorScope } from './contracts';
import IncidentOverview from './IncidentOverview';
import RuleManager from './RuleManager';
import DeviceWorkspace, { type DeviceWorkspaceView } from './DeviceWorkspace';
import AlertNavigation from '../AlertNavigation';
import './unifiedAlerts.css';

export function parseUnifiedScope(search: string): { scope?: SensorScope; error?: string } {
  const params = new URLSearchParams(search);
  if (!params.has('orgId') && !params.has('sensorId')) return {};
  const orgId = params.get('orgId') || '';
  const sensorId = params.get('sensorId') || '';
  const valid = /^[a-zA-Z0-9_-]{1,160}$/;
  if (!valid.test(orgId) || !valid.test(sensorId))
    return {
      error:
        'Linket mangler en gyldig organisation eller enhed. Åbn alarmstyringen fra enhedens side.',
    };
  return { scope: { orgId, sensorId } };
}

export function UnifiedAlertsPage() {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { scope, error } = parseUnifiedScope(search);
  const rules = pathname.endsWith('/rules');
  const suffix = scope ? `?${new URLSearchParams(scope).toString()}` : '';
  return (
    <Layout>
      <HeaderBar />
      <Layout.Content>
        <main className="unified-alerts ua-page ua-stack">
          <div className="ua-header">
            <div>
              <h1>{scope ? 'Enhedens overvågning' : rules ? 'Fælles alarmregler' : 'Hændelser'}</h1>
              <p className="ua-muted">
                {scope
                  ? 'Status, indstillinger og opfølgning for samme enhed.'
                  : rules
                  ? 'Regler på tværs af enheder. Åbn en enhed for at ændre dens opsætning.'
                  : 'Følg op på alarmer på tværs af enheder og kunder.'}
              </p>
            </div>
            <Link className="ua-link" to="/sensors">
              Enhedsoversigt
            </Link>
          </div>
          {error ? (
            <p className="ua-notice ua-error" role="alert">
              {error}
            </p>
          ) : (
            <>
              {scope ? (
                <DeviceWorkspace
                  key={`${scope.orgId}:${scope.sensorId}`}
                  {...scope}
                  view={
                    new URLSearchParams(search).get('view') === 'status'
                      ? 'status'
                      : rules
                      ? 'settings'
                      : 'incidents'
                  }
                  onViewChange={(view: DeviceWorkspaceView) => {
                    history.push(
                      `/monitoring/unified${view === 'settings' ? '/rules' : ''}${suffix}${
                        view === 'status' ? '&view=status' : ''
                      }`
                    );
                  }}
                />
              ) : (
                <>
                  <AlertNavigation active={rules ? 'rules' : 'incidents'} />
                  {!rules && <p className="ua-notice">
                    Her vises hændelser fra den fælles alarmstyring. Se Enheder og dækning for
                    enheder med ældre opsætning. Afvigelser vurderes særskilt.
                  </p>}
                  {rules ? <RuleManager /> : <IncidentOverview />}
                </>
              )}
            </>
          )}
        </main>
      </Layout.Content>
      <FooterBar />
    </Layout>
  );
}

export function SensorAlertsPanel(scope: SensorScope) {
  return <DeviceWorkspace key={`${scope.orgId}:${scope.sensorId}`} {...scope} />;
}

export default UnifiedAlertsPage;
